/**
 * Sorting and filtering for the pool CSV edit grid.
 *
 * The grid edits rows in place, so what it shows is a *view* over the rows and never the rows
 * themselves: {@link visibleRowIndices} returns source indices, and every grid coordinate has to be
 * mapped back through it before reading or writing a cell. Reordering the underlying array instead
 * would be simpler right up until an edit landed on the wrong sample.
 */

export type SortDirection = 'asc' | 'desc'

export interface ColumnSort {
  column: string
  direction: SortDirection
}

/** Column name → the substring every cell in that column must contain. Case-insensitive, ANDed. */
export type ColumnFilters = Record<string, string>

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/**
 * Blanks sort last in *both* directions — deliberately outside the ascending/descending flip. A
 * metadata column is mostly empty early on, and an ascending sort that opens on a screenful of empty
 * cells hides the very rows the user sorted to find.
 */
const compareCells = (a: string, b: string, direction: SortDirection): number => {
  if (a === '' || b === '') return a === b ? 0 : a === '' ? 1 : -1
  const ordered = collator.compare(a, b)
  return direction === 'desc' ? -ordered : ordered
}

const matchesFilters = (row: Record<string, string>, filters: ColumnFilters): boolean =>
  Object.entries(filters).every(([column, needle]) => {
    const wanted = needle.trim().toLowerCase()
    return wanted === '' || (row[column] ?? '').toLowerCase().includes(wanted)
  })

/** Source row indices, filtered and ordered as the grid should display them. */
export const visibleRowIndices = (
  rows: Record<string, string>[],
  filters: ColumnFilters = {},
  sort: ColumnSort | null = null
): number[] => {
  const indices = rows.reduce<number[]>((kept, row, index) => {
    if (matchesFilters(row, filters)) kept.push(index)
    return kept
  }, [])

  if (sort === null) return indices

  /* Ties keep source order, so repeatedly sorting one column never shuffles the rest. */
  return indices.sort(
    (a, b) => compareCells(rows[a]?.[sort.column] ?? '', rows[b]?.[sort.column] ?? '', sort.direction) || a - b
  )
}

/** Header clicks cycle ascending → descending → unsorted, so a sort can always be undone. */
export const nextSort = (current: ColumnSort | null, column: string): ColumnSort | null => {
  if (current === null || current.column !== column) return { column, direction: 'asc' }
  return current.direction === 'asc' ? { column, direction: 'desc' } : null
}

export const activeFilters = (filters: ColumnFilters): [string, string][] =>
  Object.entries(filters).filter(([, needle]) => needle.trim() !== '')

/**
 * A column name the grid can take on, or the reason it cannot. Names become CSV headers and then
 * custom-metadata keys, so a blank or a duplicate would silently collide with an existing column.
 */
export const validateNewColumn = (name: string, existing: string[]): { ok: true; name: string } | { ok: false; error: string } => {
  const trimmed = name.trim()
  if (trimmed === '') return { ok: false, error: 'Give the column a name.' }
  if (existing.some((column) => column.toLowerCase() === trimmed.toLowerCase())) {
    return { ok: false, error: `There is already a “${trimmed}” column.` }
  }
  return { ok: true, name: trimmed }
}
